import { useEffect } from "react";

import { useBrand } from "./useBrand";

/** Canonical public origin. Canonicals must not point at a preview or localhost build. */
const SITE_ORIGIN = "https://www.treetier.com";

/** Fallback share image for pages with nothing more specific. */
const DEFAULT_SHARE_IMAGE = `${SITE_ORIGIN}/assets/landing/main-logo.png`;

export type SeoOptions = {
	/** Page title without the brand suffix; the hook appends it. */
	title?: string | null;
	description?: string | null;
	/** In-app path used for the canonical and og:url. Defaults to the current path. */
	canonicalPath?: string | null;
	/** Absolute image URL for social cards. */
	imageUrl?: string | null;
	/** Open Graph object type. */
	type?: "website" | "article" | "profile";
	/**
	 * Keeps the page out of search results. Use for anything account-specific, gated, or transient —
	 * a private tree, an auth form, a signed-in-only route. It also holds the share tags at the generic
	 * site card: a page not worth indexing is not one whose contents belong on a link preview either.
	 */
	noIndex?: boolean;
	/** schema.org JSON-LD for this page, serialized into a script tag. */
	jsonLd?: Record<string, unknown> | null;
};

type MetaSelector = { attribute: "name" | "property"; key: string };

const META_TAGS: Record<string, MetaSelector> = {
	description: { attribute: "name", key: "description" },
	robots: { attribute: "name", key: "robots" },
	ogTitle: { attribute: "property", key: "og:title" },
	ogDescription: { attribute: "property", key: "og:description" },
	ogUrl: { attribute: "property", key: "og:url" },
	ogImage: { attribute: "property", key: "og:image" },
	ogType: { attribute: "property", key: "og:type" },
	ogSiteName: { attribute: "property", key: "og:site_name" },
	twitterCard: { attribute: "name", key: "twitter:card" },
	twitterTitle: { attribute: "name", key: "twitter:title" },
	twitterDescription: { attribute: "name", key: "twitter:description" },
	twitterImage: { attribute: "name", key: "twitter:image" }
};

const JSON_LD_ID = "tt-json-ld";

let bootDescription: string | null = null;

/**
 * The description `index.html` ships with, read the first time it is needed and cached. Pages with no
 * description of their own fall back to it rather than leaving the tag holding the last page's text —
 * the tags that came from the static document have no cleanup marker, so nothing else removes them.
 */
const getDefaultDescription = (): string => {
	if (bootDescription === null) {
		bootDescription = document.head.querySelector<HTMLMetaElement>("meta[name=description]")?.content ?? "";
	}

	return bootDescription;
};

/** Finds an existing meta tag or creates one, marking anything it creates so cleanup can remove it. */
const upsertMeta = (selector: MetaSelector, content: string): void => {
	const query = `meta[${selector.attribute}="${selector.key}"]`;
	let element = document.head.querySelector<HTMLMetaElement>(query);

	if (!element) {
		element = document.createElement("meta");
		element.setAttribute(selector.attribute, selector.key);
		element.dataset.ttSeo = "true";
		document.head.appendChild(element);
	}

	element.setAttribute("content", content);
};

const removeMeta = (selector: MetaSelector): void => {
	const element = document.head.querySelector<HTMLMetaElement>(
		`meta[${selector.attribute}="${selector.key}"][data-tt-seo="true"]`
	);
	element?.remove();
};

const upsertCanonical = (href: string): void => {
	let element = document.head.querySelector<HTMLLinkElement>("link[rel=canonical]");

	if (!element) {
		element = document.createElement("link");
		element.rel = "canonical";
		element.dataset.ttSeo = "true";
		document.head.appendChild(element);
	}

	element.href = href;
};

const upsertJsonLd = (data: Record<string, unknown>): void => {
	let element = document.getElementById(JSON_LD_ID) as HTMLScriptElement | null;

	if (!element) {
		element = document.createElement("script");
		element.id = JSON_LD_ID;
		element.type = "application/ld+json";
		document.head.appendChild(element);
	}

	element.textContent = JSON.stringify(data);
};

/**
 * Declares a page's search and social metadata: title, description, canonical, Open Graph, Twitter card,
 * indexability and JSON-LD. Tags are written into <head> on mount and cleaned up on unmount, so navigating
 * between routes never leaves a previous page's description or structured data behind.
 *
 * These tags are injected by JavaScript. Search engines that render pages (Google) read them; link-preview
 * scrapers that do not execute JavaScript (WhatsApp, Slack, LinkedIn, Facebook) only ever see the static
 * tags in `public/index.html`, so shared links preview generically until the app is server-rendered or a
 * bot-detecting edge layer injects these server-side. Declaring them here is the prerequisite for either.
 */
export const useSeo = ({
	title,
	description,
	canonicalPath,
	imageUrl,
	type = "website",
	noIndex = false,
	jsonLd = null
}: SeoOptions): void => {
	const { brand } = useBrand();
	// Serialized so a caller can build the object inline without re-running the effect every render.
	const jsonLdKey = jsonLd ? JSON.stringify(jsonLd) : "";

	useEffect(() => {
		const resolvedTitle = title ? `${title} | ${brand}` : brand;
		const resolvedUrl = `${SITE_ORIGIN}${canonicalPath ?? window.location.pathname}`;

		// A noIndex page falls back to the generic site card. Someone's private tree must not put its name
		// or its cover photo on a link preview, and writing the brand defaults rather than skipping the tags
		// also clears whatever the previously viewed page left in them.
		const shareTitle = noIndex ? brand : resolvedTitle;
		const shareUrl = noIndex ? SITE_ORIGIN : resolvedUrl;
		const shareImage = noIndex ? DEFAULT_SHARE_IMAGE : imageUrl || DEFAULT_SHARE_IMAGE;
		// Read unconditionally, so the first page to render snapshots the static value rather than whatever
		// an earlier page happened to leave behind.
		const defaultDescription = getDefaultDescription();
		const shareDescription = (noIndex ? null : description) || defaultDescription;

		// The tab still names the page: an owner reading their own private tree should see its title there.
		document.title = resolvedTitle;

		upsertMeta(META_TAGS.ogTitle, shareTitle);
		upsertMeta(META_TAGS.twitterTitle, shareTitle);
		upsertMeta(META_TAGS.ogUrl, shareUrl);
		upsertMeta(META_TAGS.ogType, noIndex ? "website" : type);
		upsertMeta(META_TAGS.ogSiteName, brand);
		upsertMeta(META_TAGS.ogImage, shareImage);
		upsertMeta(META_TAGS.twitterImage, shareImage);
		upsertMeta(META_TAGS.twitterCard, "summary_large_image");
		upsertCanonical(resolvedUrl);

		if (shareDescription) {
			upsertMeta(META_TAGS.description, shareDescription);
			upsertMeta(META_TAGS.ogDescription, shareDescription);
			upsertMeta(META_TAGS.twitterDescription, shareDescription);
		}

		// Only written when the page must stay out of the index; its absence is the "indexable" default.
		if (noIndex) {
			upsertMeta(META_TAGS.robots, "noindex, nofollow");
		} else {
			removeMeta(META_TAGS.robots);
		}

		if (jsonLdKey) {
			upsertJsonLd(JSON.parse(jsonLdKey) as Record<string, unknown>);
		}

		return () => {
			document.title = brand;
			document.getElementById(JSON_LD_ID)?.remove();
			removeMeta(META_TAGS.robots);
			// Tags that existed in index.html before the app booted are left alone; only ours are removed.
			[META_TAGS.ogTitle, META_TAGS.ogDescription, META_TAGS.ogUrl, META_TAGS.ogImage].forEach(removeMeta);
		};
	}, [brand, canonicalPath, description, imageUrl, jsonLdKey, noIndex, title, type]);
};

export { SITE_ORIGIN };
